import { apiService } from '@/lib/api-functions/api-service';
import { storeRegistry } from '@/rerun-manager/GenericStore';

export interface StatusWorkflow {
  [key: string]: {
    color: string;
  };
}

export interface StatusData {
  id: string;
  company_id: string;
  game_id: string | null;
  objects: StatusWorkflow;
  templates: StatusWorkflow;
  documents: StatusWorkflow;
  dialogs: StatusWorkflow;
  loca: StatusWorkflow;
  created_at: string;
}

/**
 * Store for status configurations, using GenericStore
 */
export const statusStore = storeRegistry.getStore<StatusData>({
  key: 'status',
  ttlMs: 1000 * 60 * 10, // 10 minutes cache
});

/**
 * Fetch status configurations for a company and optionally a game
 */
export async function fetchStatuses(
  companyId: string,
  gameId?: string
): Promise<StatusData[]> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    let endpoint = `/status?company_id=${companyId}`;
    if (gameId) {
      endpoint += `&game_id=${gameId}`;
    }

    const result = await apiService['request']<StatusData[]>(endpoint, {
      method: 'GET'
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch status configurations');
    }

    // Sync the store with the fetched data
    await statusStore.syncFromRemote(async () => result.data);

    return result.data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch status configurations';
    console.error('fetchStatuses - error:', err);
    throw new Error(message);
  }
}

/**
 * Create a new status configuration
 */
export async function createStatus(
  companyId: string,
  gameId: string | null,
  workflows: {
    objects?: StatusWorkflow;
    templates?: StatusWorkflow;
    documents?: StatusWorkflow;
    dialogs?: StatusWorkflow;
    loca?: StatusWorkflow;
  }
): Promise<StatusData> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService['request']<StatusData>('/status', {
      method: 'POST',
      body: JSON.stringify({
        company_id: companyId,
        game_id: gameId,
        ...workflows
      })
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create status configuration');
    }

    // Add to store
    const currentData = statusStore.getData();
    const updatedData = [...currentData, result.data];
    statusStore.setData(updatedData);

    return result.data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create status configuration';
    console.error('createStatus - error:', err);
    throw new Error(message);
  }
}

/**
 * Update an existing status configuration
 */
export async function updateStatus(
  id: string,
  workflows: {
    objects?: StatusWorkflow;
    templates?: StatusWorkflow;
    documents?: StatusWorkflow;
    dialogs?: StatusWorkflow;
    loca?: StatusWorkflow;
  }
): Promise<StatusData> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService['request']<StatusData>('/status', {
      method: 'PUT',
      body: JSON.stringify({
        id,
        ...workflows
      })
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update status configuration');
    }

    // Update in store
    const currentData = statusStore.getData();
    const updatedData = currentData.map(item => 
      item.id === id ? result.data : item
    );
    statusStore.setData(updatedData);

    return result.data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update status configuration';
    console.error('updateStatus - error:', err);
    throw new Error(message);
  }
}

/**
 * Delete a status configuration
 */
export async function deleteStatus(id: string): Promise<void> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService['request']<void>(`/status?id=${id}`, {
      method: 'DELETE'
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete status configuration');
    }

    // Remove from store
    const currentData = statusStore.getData();
    statusStore.setData(currentData.filter(item => item.id !== id));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete status configuration';
    console.error('deleteStatus - error:', err);
    throw new Error(message);
  }
}

/**
 * Get the default workflow steps
 */
export function getDefaultWorkflow(): StatusWorkflow {
  return {
    "draft": { "color": "#d3d933" },
    "review": { "color": "#354dff" },
    "approved": { "color": "#a9d18e" },
    "rejected": { "color": "#ffc000" },
    "published": { "color": "#6e35ff" },
    "implemented": { "color": "#8faadc" }
  };
}

